// Admin Stripe Manager - Business logic for Stripe transaction analytics
// Handles transaction data, chart aggregation, and display options

import { Manager } from '../../../../../core';
import { logger } from '../../../../../core';

// ========== TEMP: Mock data - remove when no longer needed ==========
const PLANS = ["pro", "max"];
const TIERS = ["basic", "enhanced"];
const BILLING = ["monthly", "annual", "one-time"];
const MONTHLY_CENTS = { pro: { basic: 1999, enhanced: 3999 }, max: { basic: 17499, enhanced: 13124 } };
function generateMockTransactions(count = 100) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const plan = PLANS[Math.floor(Math.random() * PLANS.length)];
    const tier = TIERS[Math.floor(Math.random() * TIERS.length)];
    const billing = BILLING[Math.floor(Math.random() * BILLING.length)];
    const monthly = MONTHLY_CENTS[plan][tier];
    const subtotal = billing === "monthly" ? monthly : billing === "annual" ? monthly * 12 : monthly * 36;
    const hasDiscount = Math.random() > 0.6;
    const discountAmount = hasDiscount ? Math.floor(subtotal * (0.1 + Math.random() * 0.4)) : 0;
    const amount = Math.max(0, subtotal - discountAmount);
    const daysAgo = Math.floor(Math.random() * 60);
    const d = new Date(now);
    d.setDate(d.getDate() - daysAgo);
    out.push({
      _id: `mock_${i}`,
      amount,
      status: "succeeded",
      metadata: {
        planId: plan,
        tierId: tier,
        billingCycle: billing,
        ...(hasDiscount ? { discountAmount, subtotal, promotionCode: { id: "mock", code: "MOCK", active: true } } : {}),
      },
      createdAt: { $date: d.toISOString() },
      updatedAt: { $date: d.toISOString() },
    });
  }
  return out;
}
// ========== END TEMP ==========

const PLAN_COLORS = {
  free: '#6b7280',
  pro: '#3b82f6',
  max: '#8b0000',
  test: '#eab308',
  default: '#94a3b8',
};

// Tier color modifiers (lighter/darker) for byTier view
const TIER_SUFFIX_COLORS = {
  basic: 1,
  enhanced: 0.75,
  default: 0.9,
};

export class AdminStripeManager extends Manager {
  constructor() {
    super();
    this.transactions = [];
    this.chartView = 'all'; // 'all' | 'byPlan' | 'byTier'
    this.amountMode = 'actual'; // 'actual' | 'full' - actual paid vs pre-discount
    this.timeRange = '30d'; // '7d' | '30d' | '90d'
    this.useMockData = false; // TEMP: toggle mock data for dev - remove when no longer needed
  }

  async initGlobalVariables() {
    this.transactions = [];
    this.chartView = 'all';
    this.amountMode = 'actual';
    this.timeRange = '30d';
    this.useMockData = false;
  }

  async initSystems() {
    await this.loadTransactions();
    this.isInitialized = true;
    logger.log(
      { tags: 'admin-stripe-manager', color1: 'blue' },
      'AdminStripeManager initialized successfully'
    );
  }

  /**
   * Load transactions from admin API (works in prod and dev)
   */
  async loadTransactions() {
    try {
      const response = await fetch(
        `/api/admin/stripe/transactions?limit=200`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        this.transactions = data.transactions || [];
      } else {
        // Fallback: use empty array (or mock data for development)
        this.transactions = [];
      }
    } catch (err) {
      logger.error(
        { tags: 'admin-stripe-manager|error', color1: 'red' },
        'Failed to load transactions:',
        err
      );
      this.transactions = [];
    }
  }

  getTransactions() {
    return this.transactions;
  }

  // TEMP: Mock data toggle - remove when no longer needed
  getUseMockData() {
    return this.useMockData;
  }

  setUseMockData(use) {
    this.useMockData = !!use;
  }

  getEffectiveTransactions() {
    return this.useMockData ? generateMockTransactions(100) : this.transactions;
  }

  getChartView() {
    return this.chartView;
  }

  setChartView(view) {
    if (view === 'all' || view === 'byPlan' || view === 'byTier') {
      this.chartView = view;
    }
  }

  getAmountMode() {
    return this.amountMode;
  }

  setAmountMode(mode) {
    if (mode === 'actual' || mode === 'full') {
      this.amountMode = mode;
    }
  }

  getTimeRange() {
    return this.timeRange;
  }

  setTimeRange(range) {
    if (['7d', '30d', '90d'].includes(range)) {
      this.timeRange = range;
    }
  }

  /**
   * Extract planId from transaction (metadata or nested subscription)
   */
  getPlanIdFromTransaction(t) {
    const meta = t.metadata || {};
    if (meta.planId) return meta.planId;
    const sub = t.stripeSubscriptionId;
    if (sub && typeof sub === 'object') {
      const plan = sub.plan || sub.items?.data?.[0]?.plan;
      return plan?.metadata?.planId || 'unknown';
    }
    return 'unknown';
  }

  /**
   * Extract tierId from transaction (metadata or nested subscription)
   */
  getTierIdFromTransaction(t) {
    const meta = t.metadata || {};
    if (meta.tierId) return meta.tierId;
    const sub = t.stripeSubscriptionId;
    if (sub && typeof sub === 'object') {
      const plan = sub.plan || sub.items?.data?.[0]?.plan;
      return plan?.metadata?.tierId || 'basic';
    }
    return 'basic';
  }

  /**
   * Get grouping key for chart: planId, planId-tierId, or 'all'
   * @param {Object} t - transaction
   * @param {string} [chartView] - override chartView
   */
  getGroupKey(t, chartView) {
    const view = chartView ?? this.chartView;
    if (view === 'byTier') {
      const planId = this.getPlanIdFromTransaction(t);
      const tierId = this.getTierIdFromTransaction(t);
      return `${planId}-${tierId}`;
    }
    if (view === 'byPlan') {
      return this.getPlanIdFromTransaction(t);
    }
    return 'all';
  }

  /**
   * Get amount in dollars for display.
   * amountMode 'actual' = amount paid; 'full' = subtotal (pre-discount) when available
   * @param {Object} t - transaction
   * @param {string} [amountMode] - override amountMode
   */
  getAmountForDisplay(t, amountMode) {
    const mode = amountMode ?? this.amountMode;
    let cents = typeof t.amount === 'number' ? t.amount : 0;
    if (mode === 'full') {
      const meta = t.metadata || {};
      if (typeof meta.subtotal === 'number') {
        cents = meta.subtotal;
      }
    }
    return cents / 100;
  }

  /**
   * Get amount in dollars (legacy - uses actual amount)
   * @deprecated Use getAmountForDisplay for amount-mode-aware values
   */
  getAmountDollars(t) {
    return this.getAmountForDisplay(t);
  }

  /**
   * Get date for grouping (use createdAt or updatedAt)
   */
  getTransactionDate(t) {
    const d = t.createdAt || t.updatedAt;
    if (d?.$date) return new Date(d.$date);
    if (d instanceof Date) return d;
    if (typeof d === 'string') return new Date(d);
    return new Date();
  }

  /**
   * Build chart data: [{ date, total, byPlan: { pro: 10, 'pro-basic': 5, ... } }]
   * byPlan key is planId for byPlan view, planId-tierId for byTier view
   * @param {Object} [opts] - { chartView, amountMode } overrides
   */
  getChartData(opts = {}) {
    const { chartView, amountMode } = opts;
    const view = chartView ?? this.chartView;
    const mode = amountMode ?? this.amountMode;
    const txns = this.getEffectiveTransactions();
    const succeeded = txns.filter((t) => t.status === 'succeeded');
    const byDate = {};

    for (const t of succeeded) {
      const date = this.getTransactionDate(t);
      const dateKey = date.toISOString().slice(0, 10);
      const amountDollars = this.getAmountForDisplay(t, mode);
      const groupKey = this.getGroupKey(t, view);

      if (!byDate[dateKey]) {
        byDate[dateKey] = { date: dateKey, total: 0, byPlan: {} };
      }
      byDate[dateKey].total += amountDollars;
      byDate[dateKey].byPlan[groupKey] = (byDate[dateKey].byPlan[groupKey] || 0) + amountDollars;
    }

    const sorted = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
    return sorted;
  }

  /**
   * Get summary stats (uses amountMode for revenue values)
   * @param {Object} [opts] - { chartView, amountMode } overrides
   */
  getSummaryStats(opts = {}) {
    const { chartView, amountMode } = opts;
    const view = chartView ?? this.chartView;
    const mode = amountMode ?? this.amountMode;
    const txns = this.getEffectiveTransactions();
    const succeeded = txns.filter((t) => t.status === 'succeeded');
    const totalRevenue = succeeded.reduce((sum, t) => sum + this.getAmountForDisplay(t, mode), 0);
    const totalCount = succeeded.length;
    const pending = txns.filter((t) => t.status === 'pending').length;
    const failed = txns.filter((t) => t.status === 'failed').length;

    const byPlan = {};
    for (const t of succeeded) {
      const groupKey = this.getGroupKey(t, view);
      byPlan[groupKey] = (byPlan[groupKey] || 0) + this.getAmountForDisplay(t, mode);
    }

    const totalDiscount = succeeded.reduce((sum, t) => {
      const meta = t.metadata || {};
      const discountCents = typeof meta.discountAmount === 'number' ? meta.discountAmount : 0;
      return sum + discountCents / 100;
    }, 0);

    return {
      totalRevenue,
      totalCount,
      pending,
      failed,
      byPlan,
      totalDiscount,
      amountMode: mode,
    };
  }

  getPlanColor(planIdOrKey) {
    const basePlanId = String(planIdOrKey).split('-')[0];
    return PLAN_COLORS[basePlanId] || PLAN_COLORS.default;
  }

  getGroupColor(groupKey) {
    const basePlanId = String(groupKey).split('-')[0];
    const baseColor = PLAN_COLORS[basePlanId] || PLAN_COLORS.default;
    if (groupKey.includes('-')) {
      const tierPart = groupKey.split('-').slice(1).join('-');
      const modifier = TIER_SUFFIX_COLORS[tierPart] ?? TIER_SUFFIX_COLORS.default;
      return this._adjustColorBrightness(baseColor, modifier);
    }
    return baseColor;
  }

  _adjustColorBrightness(hex, factor) {
    const match = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (!match) return hex;
    const r = Math.min(255, Math.round(parseInt(match[1], 16) * factor));
    const g = Math.min(255, Math.round(parseInt(match[2], 16) * factor));
    const b = Math.min(255, Math.round(parseInt(match[3], 16) * factor));
    return `rgb(${r},${g},${b})`;
  }

  async refreshTransactions() {
    await this.loadTransactions();
  }
}
